import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Textarea from '@mui/joy/Textarea';
import FormControl from '@mui/joy/FormControl';
import FormLabel from '@mui/joy/FormLabel';
import Stack from '@mui/joy/Stack';
import CircularProgress from '@mui/joy/CircularProgress';
import { brandsApi } from '../../api/brands.api';
import { CreateBrandDto } from '../../types/brand';
import { formatRecordReference } from '../../utils/formatRecordReference';

const BrandForm: React.FC = () => {
  const navigate = useNavigate();
  const { reference } = useParams<{ reference?: string }>();
  const brandReference = reference as string | undefined;
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateBrandDto>({
    name: '',
    description: '',
    logo: '',
  });

  useEffect(() => {
    if (brandReference) {
      loadBrand(brandReference);
    }
  }, [brandReference]);

  const loadBrand = async (ref: string) => {
    try {
      setLoading(true);
      const brand = await brandsApi.getOne(ref);
      setFormData({
        name: brand.name,
        description: brand.description || '',
        logo: brand.logo || '',
      });
    } catch (error) {
      console.error('Error al cargar marca:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      if (brandReference) {
        await brandsApi.update(brandReference, formData);
      } else {
        await brandsApi.create(formData);
      }
      navigate('/brands');
    } catch (error) {
      console.error('Error al guardar marca:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  if (loading && brandReference) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, maxWidth: 640, mx: 'auto', width: '100%' }}>
      <Typography level="h2" sx={{ mb: 2 }}>
        {brandReference
          ? `Editar marca #${formatRecordReference(brandReference)}`
          : 'Nueva marca'}
      </Typography>
      <Sheet variant="outlined" sx={{ p: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <FormControl required>
              <FormLabel>Nombre</FormLabel>
              <Input name="name" value={formData.name} onChange={handleChange} />
            </FormControl>
            <FormControl>
              <FormLabel>Descripción</FormLabel>
              <Textarea name="description" value={formData.description} onChange={handleChange} minRows={3} />
            </FormControl>
            <FormControl>
              <FormLabel>URL del logo</FormLabel>
              <Input
                name="logo"
                type="text"
                value={formData.logo}
                onChange={handleChange}
                placeholder="https://ejemplo.com/logo.png"
              />
              {formData.logo ? (
                <Box sx={{ mt: 1 }}>
                  <Typography level="body-xs" sx={{ color: 'neutral.500', mb: 0.5 }}>
                    Vista previa
                  </Typography>
                  <Box
                    component="img"
                    src={formData.logo}
                    alt=""
                    sx={{
                      height: 48,
                      width: 'auto',
                      maxWidth: 200,
                      objectFit: 'contain',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 'sm',
                      p: 0.5,
                    }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).src =
                        'https://via.placeholder.com/150x50?text=Logo+no+disponible';
                    }}
                  />
                </Box>
              ) : null}
            </FormControl>
            <Stack direction="row" spacing={1} justifyContent="space-between" sx={{ pt: 1 }}>
              <Button type="submit" color="primary" loading={loading}>
                {brandReference ? 'Actualizar' : 'Crear'}
              </Button>
              <Button type="button" variant="outlined" color="neutral" onClick={() => navigate('/brands')}>
                Cancelar
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Sheet>
    </Box>
  );
};

export default BrandForm;
